<script lang="ts">
    import { onMount } from 'svelte';
    import { theme } from '../stores/theme';
    import { sidebar } from '../stores/sidebar';
    import { systemStatus } from '../stores/status';
    import { locale, t, SUPPORTED_LOCALES, type Locale } from '../stores/locale';
    import StatusDot from './StatusDot.svelte';
    import Icon from './Icon.svelte';
    import Button from './forms/Button.svelte';
    import { push } from 'svelte-spa-router';
    import { tooltip } from '../lib/tooltip';

    let dropdownOpen = false;
    let closeTimer: number;

    function toggleDropdown(event: Event) {
        event.stopPropagation();
        dropdownOpen = !dropdownOpen;
    }

    function selectLocale(loc: Locale, event: Event) {
        event.stopPropagation();
        locale.set(loc);
        dropdownOpen = false;
    }

    function handleMouseLeave() {
        closeTimer = window.setTimeout(() => {
            dropdownOpen = false;
        }, 300);
    }

    function handleMouseEnter() {
        clearTimeout(closeTimer);
    }

    function handleClickOutside(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (!target.closest('.lang-dropdown')) {
            dropdownOpen = false;
        }
    }

    onMount(() => {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    });
</script>

<header class="app-header">
    <div class="header-section header-left">
        <div class="menu-wrap">
            <Button
                variant="icon"
                on:click={sidebar.toggle}
                aria-label="Toggle menu"
            >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                    <rect y="2"  width="18" height="2" rx="1" fill="currentColor"/>
                    <rect y="8"  width="18" height="2" rx="1" fill="currentColor"/>
                    <rect y="14" width="18" height="2" rx="1" fill="currentColor"/>
                </svg>
            </Button>
        </div>
    </div>

    <div class="header-section header-center">
        <span class="app-title">LISTmod-HU</span>
        <div use:tooltip={"System status: " + $systemStatus} style="display: flex;">
            <Button
                variant="icon"
                on:click={() => { push('/')}}
                aria-label="System status"
            >
                <StatusDot status={$systemStatus} large />
            </Button>
        </div>
    </div>

    <div class="header-section header-right">
        <div use:tooltip={$t('header.toggleTheme')} style="display: flex;">
            <Button
                variant="icon"
                class={$theme === 'dark' ? 'dark' : ''}
                on:click={theme.toggle}
                aria-label={$t('header.toggleTheme')}
            >
                <!-- Sun — visible in dark mode -->
                <svg class="icon icon-sun" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="5"/>
                    <line x1="12" y1="1"  x2="12" y2="3"/>
                    <line x1="12" y1="21" x2="12" y2="23"/>
                    <line x1="4.22"  y1="4.22"  x2="5.64"  y2="5.64"/>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                    <line x1="1"  y1="12" x2="3"  y2="12"/>
                    <line x1="21" y1="12" x2="23" y2="12"/>
                    <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36"/>
                    <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"/>
                </svg>
                <!-- Moon — visible in light mode -->
                <svg class="icon icon-moon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
            </Button>
        </div>
        <div class="lang-dropdown" on:mouseleave={handleMouseLeave} on:mouseenter={handleMouseEnter}>
            <div use:tooltip={$t('header.selectLanguage')} style="display: flex;">
                <Button
                    variant="icon"
                    on:click={toggleDropdown}
                    aria-label={$t('header.selectLanguage')}
                    aria-expanded={dropdownOpen}
                    class="lang-trigger"
                >
                    <Icon name="globe" size={20} />
                    <span class="current-lang-code">{$locale.toUpperCase()}</span>
                </Button>
            </div>
            {#if dropdownOpen}
                <div class="lang-menu">
                    {#each SUPPORTED_LOCALES as loc}
                        <Button
                            mode="text"
                            disabled={$locale === loc.code}
                            variant={$locale === loc.code ? 'primary' : 'default'}
                            fullWidth
                            class="lang-option "
                            on:click={(e) => selectLocale(loc.code, e)}
                        >
                            <span class="lang-code {$locale === loc.code ? 'active' : ''}">{loc.code.toUpperCase()}</span>
                            <span class="lang-name">{loc.name}</span>
                        </Button>
                    {/each}
                </div>
            {/if}
        </div>
    </div>
</header>

<style lang="scss">
    .app-header {
        display: flex;
        align-items: center;
        height: $header-height;
        padding: 0 1.5em; // Match main padding
        background-color: transparent; // Shows the white sidebar-bg from app-layout
        /* border-bottom: 1px solid $color-header-border; */
        position: sticky;
        top: 0;
        z-index: 100;
        flex-shrink: 0;
        transition: background-color 0.2s ease, border-color 0.2s ease;
    }

    .header-section {
        display: flex;
        align-items: center;
        flex: 1;

        &.header-center {
            justify-content: center;
            gap: 0.5em;
        }

        &.header-right {
            justify-content: flex-end;
            gap: 0.5em;
        }
    }

    .menu-wrap {
        @media (min-width: $breakpoint-md) {
            display: none;
        }
    }

    .app-title {
        font-size: 1em;
        font-weight: 500;
        color: $color-primary;
        letter-spacing: 0.05em;
        user-select: none;
    }

    // ── Theme toggle ──────────────────────────────────────────────────────────

    .icon {
        position: absolute;
        transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        pointer-events: none;
    }

    // Light mode: moon visible, sun hidden
    .icon-sun  { opacity: 0; transform: rotate(-90deg); }
    .icon-moon { opacity: 1; transform: rotate(0deg); }

    // Dark mode: sun visible, moon hidden
    :global(.btn.dark) .icon-sun  { opacity: 1; transform: rotate(0deg); }
    :global(.btn.dark) .icon-moon { opacity: 0; transform: rotate(90deg); }

    // ── Language selector ──────────────────────────────────────────────────────

    .lang-dropdown {
        position: relative;
    }

    :global(.btn.lang-trigger) {
        width: auto !important;
        padding: 0 10px !important;
        border-radius: 20px !important;
        
    }

    .current-lang-code {
        font-size: 0.85rem; // Bigger
        font-weight: 600;
        letter-spacing: 0.05em;
    }

    .lang-code {
        font-size: 0.75em;
        font-weight: 700;
        color: var(--color-text-muted);
        min-width: 24px;

        &.active {
            color: $color-primary;
        }
    }

    .lang-menu {
        position: absolute;
        top: calc(100% + 8px); // Slightly more gap
        right: 0;
        background-color: var(--color-menu-bg);
        border-radius: 12px;
        padding: 6px;
        min-width: 150px; // Slightly wider to ensure all buttons fit
        z-index: 150;
    }

    .lang-name {
        flex: 1;
        text-align: left;
    }
</style>
